// client/src/pages/About.js
import React, { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function About() {
  const [totalTasks, setTotalTasks] = useState(null);
  const [totalRecovered, setTotalRecovered] = useState(null);
  const [showConnect, setShowConnect] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/tasks/stats/tasks-count`);
        const d = await res.json();
        setTotalTasks(d.totalTasks ?? 0);
      } catch (err) {
        console.error("Failed to load stats", err);
      }
    })();
    (async () => {
      try {
        const res = await fetch(`${API}/lost-found/stats/success-count`);
        const d = await res.json();
        setTotalRecovered(d.totalRecovered ?? 0);
      } catch (err) {
        console.error("Failed to load recovered stat", err);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-slate-100 transition-colors px-6 py-12">
      <div className="max-w-4xl mx-auto text-center mb-14">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-indigo-600 dark:text-indigo-400">
          About CollegePal
        </h1>

        <p className="mt-4 text-lg text-gray-600 dark:text-slate-300">
          CollegePal is a student-built platform designed to solve real problems inside college campuses — from getting help, to finding people, to building clubs and communities.
        </p>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {/* STORY */}
          <div className="p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-md">
            <h2 className="text-2xl font-bold mb-3">Why CollegePal?</h2>
            <p className="leading-relaxed text-gray-700 dark:text-slate-300 text-justify">
              Inside every college, students need help, lose items, want to form clubs, or look for people with specific skills.
              <span className="text-indigo-600 dark:text-indigo-300"> The problem was never talent — it was connection.</span>
            </p>

            <p className="leading-relaxed text-gray-700 dark:text-slate-300 text-justify mt-4">
              CollegePal was built to bridge this gap by giving students a single trusted platform to collaborate, communicate, and grow together — without external noise.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="p-4 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                <p className="text-xs text-indigo-700 dark:text-indigo-300">Tasks & Help</p>
                <p className="font-semibold text-xl mt-1">
                  {totalTasks !== null ? `${totalTasks}+ Tasks` : "Loading..."}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Posted by students for students
                </p>
              </div>

              <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                <p className="text-xs text-purple-700 dark:text-purple-300">Live Interaction</p>
                <p className="font-semibold text-xl mt-1">Real-time Chat</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Instant messaging & group chats
                </p>
              </div>
            </div>
          </div>

          {/* FEATURES */}
          <div className="p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-md">
            <h2 className="text-2xl font-bold mb-4">What You Can Do</h2>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300">
              <li>• 📌 <b>Post & Complete Tasks</b> — Get help or earn by helping others.</li>
              <li>• 🔍 <b>Lost & Found</b> — Report lost items and return them securely.</li>
              <li>• 🏫 <b>Recruit / Clubs</b> — Create clubs, recruit members, and manage selections.</li>
              <li>• 💬 <b>Chats & Group Chats</b> — Communicate instantly with task owners, members, or clubs.</li>
              <li>• 🔔 <b>Smart Notifications</b> — Never miss updates, selections, or messages.</li>
            </ul>
          </div>

          {/* VISION */}
          <div className="p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-md">
            <h2 className="text-2xl font-bold mb-3">Our Vision</h2>
            <p className="leading-relaxed text-gray-700 dark:text-slate-300 text-justify">
              CollegePal aims to become a trusted campus ecosystem where students can safely collaborate, build experience, form communities, and support each other — all within their college environment.
            </p>
          </div>
        </div>

        {/* SIDEBAR */}
        <div>
          <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg sticky top-20 text-center">
            <img
              src="/logo.png"
              className="w-28 h-28 mx-auto rounded-full object-cover ring-4 ring-indigo-200 dark:ring-transparent opacity-90"
              alt="Founder"
            />
            <h3 className="text-xl font-bold mt-4">Harsh Vardhan</h3>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Founder • CollegePal
            </p>
            <p className="mt-4 text-sm leading-relaxed text-gray-700 dark:text-slate-300">
              A CSE student focused on building clean, practical systems that solve real college-level problems.
            </p>
            <button
              onClick={() => setShowConnect(true)}
              className="mt-5 w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-lg shadow transition"
            >
              Connect
            </button>
          </div>

          <div className="mt-6 p-6 bg-gradient-to-r from-indigo-50 to-white dark:from-indigo-900/10 dark:to-slate-800 rounded-2xl shadow-lg text-center">
            <p className="text-sm text-indigo-700 dark:text-indigo-300">
              Items successfully returned
            </p>
            <div className="mt-2 flex items-center justify-center gap-3">
              <div className="text-4xl font-extrabold text-indigo-600 dark:text-indigo-400">
                {totalRecovered !== null ? totalRecovered : "—"}
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                students helped
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-500 dark:text-slate-500 text-center">
            Built with ❤️ by students, for students.
          </p>
        </div>
      </div>

      {/* CONNECT MODAL */}
      {showConnect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-11/12 max-w-md shadow-xl">
            <h3 className="text-lg font-bold">Connect with Harsh</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
              Reach out on socials or email
            </p>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">LinkedIn</div>
                  <div className="text-xs text-slate-500">
                    harsh-vardhan-27ab76291
                  </div>
                </div>
                <a
                  className="text-indigo-600"
                  href="https://www.linkedin.com/in/harsh-vardhan-27ab76291"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open
                </a>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Email</div>
                  <div className="text-xs text-slate-500">
                    harshvardhan18102007@gmail.com
                  </div>
                </div>
                <a
                  className="text-indigo-600"
                  href="mailto:harshvardhan18102007@gmail.com"
                >
                  Mail
                </a>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowConnect(false)}
                className="px-4 py-2 border rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
